import styled from "@emotion/styled";
import {
  Checkbox,
  Container,
  Radio,
  RadioGroup,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Table,
  Paper,
} from "@mui/material";
import React from "react";
import IndicatorsUtils from "services/indicators/utils";
import { useIntl } from "react-intl";
import MapUtils from "services/map/utils";
import CommonUtils from "services/common/utils";

const styles = {
  childCheckbox: {
    marginLeft: "5px",
  },
  tableRow: {
    backgroundColor: "lightgrey",
  },
};

const HoverTableCell = styled(TableCell)`
  cursor: pointer;
  width: 15%;
`;

const CenteredHoverTableCell = styled(HoverTableCell)`
  text-align: center;
  font-weight: bold;
  line-height: unset;
`;

const IndicatorTablePaper = styled(Paper)`
  max-width: 100%;
  overflow: auto;
`;

const IndicatorRadioGroup = styled(RadioGroup)`
  min-width: max-content;
`;

const IndicatorTableCell = styled(TableCell)`
  width: 400px;
`;

const AreaCell = ({
  category,
  indicatorIndex,
  index,
  handleCellClick,
  handleCellMouseEnter,
  handleCellMouseLeave,
  getHoveredCellStyle,
  item,
}) => {
  return (
    <HoverTableCell
      key={`indicator-category-item-${category}`}
      onClick={() => handleCellClick(indicatorIndex, index)}
      onMouseEnter={() => handleCellMouseEnter(indicatorIndex, index)}
      onMouseLeave={handleCellMouseLeave}
      align="center"
    >
      <span
        style={
          typeof item[category]?.result === "number"
            ? getHoveredCellStyle({
                indicatorIndex,
                index,
                category,
              })
            : {}
        }
      >
        {!item[category]?.result
          ? "--"
          : CommonUtils.formatNumber(item[category].result)}
      </span>
    </HoverTableCell>
  );
};

const CategoryCell = ({
  getPropertyTotal,
  handleCellClick,
  handleCellMouseEnter,
  handleCellMouseLeave,
  category,
  indicator,
  indicatorIndex,
  getHoveredCellStyle,
}) => {
  return (
    <HoverTableCell
      key={`indicator-category-cell-${category}`}
      onClick={() => handleCellClick(indicatorIndex, -1)}
      onMouseEnter={() => handleCellMouseEnter(indicatorIndex, -1)}
      onMouseLeave={handleCellMouseLeave}
      align="center"
    >
      <span
        style={
          typeof getPropertyTotal(indicator.items, category) === "number"
            ? getHoveredCellStyle({ indicatorIndex, category })
            : {}
        }
      >
        {CommonUtils.formatNumber(getPropertyTotal(indicator.items, category))}
      </span>
    </HoverTableCell>
  );
};

export default function IndicatorsTable({
  indicators,
  selectedColumn,
  setSelectedColumn,
  selectedIndicators,
  setSelectedIndicators,
  hoveredIndicator,
  setHoveredIndicator,
  leafletMap,
  categories,
}) {
  const intl = useIntl();

  const onColumnClick = (type) => {
    setSelectedColumn(IndicatorsUtils.INDICATORS_TYPES[type]);
  };

  const getPropertyTotal = (items, property) => {
    const result = items
      .map((item) => {
        if (item[property]?.result) {
          return item[property].result;
        }

        return 0;
      })
      .reduce((a, b) => a + b, 0);

    return Number(result) === 0 ? "--" : result;
  };

  const getTypeByCategory = (category) => {
    switch (category) {
      case IndicatorsUtils.INDICATORS_COLUMNS.CARS:
        return IndicatorsUtils.INDICATORS_TYPES.CARS;
      case IndicatorsUtils.INDICATORS_COLUMNS.DELIVERY:
        return IndicatorsUtils.INDICATORS_TYPES.DELIVERY;
      case IndicatorsUtils.INDICATORS_COLUMNS.TRUCK:
        return IndicatorsUtils.INDICATORS_TYPES.TRUCK;
      case IndicatorsUtils.INDICATORS_COLUMNS.BUS:
        return IndicatorsUtils.INDICATORS_TYPES.BUS;
      case IndicatorsUtils.INDICATORS_COLUMNS.TWO_WHEELER:
        return IndicatorsUtils.INDICATORS_TYPES.TWO_WHEELER;
      case IndicatorsUtils.INDICATORS_COLUMNS.BATTERY:
        return IndicatorsUtils.INDICATORS_TYPES.BATTERY;
      case IndicatorsUtils.INDICATORS_COLUMNS.PEDESTRIAN:
        return IndicatorsUtils.INDICATORS_TYPES.PEDESTRIAN;
      default:
        return "";
    }
  };

  const handleAreaSelection = (isSelected, indicatorIndex, areaIndex) => {
    if (isSelected) {
      const selectedIndicatorsCopy = structuredClone(selectedIndicators);
      selectedIndicatorsCopy[indicatorIndex].checked = false;
      selectedIndicatorsCopy[indicatorIndex].areasChecked.push({
        index: areaIndex,
        uuid: crypto.randomUUID(),
      });
      setSelectedIndicators(selectedIndicatorsCopy);
      return;
    }

    const selectedIndicatorsCopy = structuredClone(selectedIndicators);

    selectedIndicatorsCopy[indicatorIndex].areasChecked.splice(
      selectedIndicators[indicatorIndex].areasChecked.findIndex(
        (obj) => obj.index === areaIndex
      ),
      1
    );

    setSelectedIndicators(selectedIndicatorsCopy);
  };

  const handleIndicatorSelection = (isSelected, indicatorIndex) => {
    const selectedIndicatorsCopy = structuredClone(selectedIndicators);
    selectedIndicatorsCopy[indicatorIndex].uuid = crypto.randomUUID();
    selectedIndicatorsCopy[indicatorIndex].checked = isSelected;

    if (selectedIndicators[indicatorIndex].areasChecked.length > 0) {
      selectedIndicatorsCopy[indicatorIndex].areasChecked = [];
    }

    setSelectedIndicators(selectedIndicatorsCopy);
  };

  const handleCellClick = (indicatorIndex, areaIndex) => {
    if (areaIndex === -1 && selectedIndicators[indicatorIndex]?.checked) {
      const contractPoints = indicators[indicatorIndex].items.map((area) =>
        Object.values(
          area[IndicatorsUtils.INDICATORS_COLUMNS[selectedColumn]]?.location ??
            {}
        )?.reverse()
      );

      if (contractPoints.flat().length > 0) {
        return leafletMap?.leafletElement.fitBounds(
          [MapUtils.getCenterOfPoints(contractPoints)?.reverse()],
          {
            padding: [50, 50],
          }
        );
      }
    }

    if (
      !selectedIndicators[indicatorIndex].areasChecked
        .map((area) => area.index)
        .includes(areaIndex)
    ) {
      return;
    }

    const position = Object.values(
      indicators[indicatorIndex].items[areaIndex][
        IndicatorsUtils.INDICATORS_COLUMNS[selectedColumn]
      ]?.location ?? {}
    )?.reverse();

    if (position.length === 0) {
      return;
    }

    leafletMap?.leafletElement.fitBounds([position], {
      padding: [50, 50],
    });
  };

  const handleCellMouseEnter = (indicatorIndex, areaIndex) => {
    if (areaIndex === -1) {
      setHoveredIndicator(selectedIndicators[indicatorIndex]?.uuid);
      return;
    }

    const uuidHovered = selectedIndicators[indicatorIndex]?.areasChecked?.find(
      (area) => area.index === areaIndex
    )?.uuid;

    if (uuidHovered) {
      setHoveredIndicator(uuidHovered);
      return;
    }

    setHoveredIndicator(null);
  };

  const handleCellMouseLeave = () => {
    setHoveredIndicator(null);
  };

  const getHoveredCellStyle = ({ indicatorIndex, index, category }) => {
    const isCellColumnSelected =
      IndicatorsUtils.INDICATORS_COLUMNS[selectedColumn] === category;

    const cellStyle = {
      textDecoration: "underline",
      textDecorationColor: IndicatorsUtils.INDICATORS_COLORS.HOVER,
      textDecorationThickness: "2px",
    };

    if (index === undefined) {
      const isContractCellChecked = selectedIndicators[indicatorIndex].checked;
      const isContractCellHovered =
        selectedIndicators[indicatorIndex].uuid === hoveredIndicator;

      return isContractCellChecked &&
        isCellColumnSelected &&
        isContractCellHovered
        ? cellStyle
        : {};
    }

    const isCellChecked = selectedIndicators[indicatorIndex].areasChecked
      .map((area) => area.index)
      .includes(index);

    const isCellHovered =
      selectedIndicators[indicatorIndex].areasChecked.find(
        (area) => area.index === index
      )?.uuid === hoveredIndicator;

    return isCellChecked && isCellHovered && isCellColumnSelected
      ? cellStyle
      : {};
  };

  return (
    <Container>
      <IndicatorTablePaper>
        <IndicatorRadioGroup row>
          <Table size="small">
            <TableHead>
              <TableRow>
                <IndicatorTableCell></IndicatorTableCell>
                {categories.map((category, index) => {
                  return (
                    <CenteredHoverTableCell
                      key={`indicator-category-column-${index}`}
                      onClick={() => onColumnClick(getTypeByCategory(category))}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Radio
                          size="small"
                          checked={
                            selectedColumn === getTypeByCategory(category)
                          }
                        />
                        <span>
                          {
                            intl.messages[
                              `indicatorsScene.table.header.${category
                                .replace("nb_", "")
                                .toLowerCase()}`
                            ]
                          }
                        </span>
                      </div>
                    </CenteredHoverTableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {indicators.map((indicator, indicatorIndex) => {
                return (
                  <React.Fragment key={`indicator-item-${indicatorIndex}`}>
                    <TableRow style={styles.tableRow}>
                      <HoverTableCell
                        component="th"
                        scope="row"
                        align="left"
                        onMouseEnter={() =>
                          indicators.length > 1 &&
                          handleCellMouseEnter(indicatorIndex, -1)
                        }
                        onMouseLeave={() =>
                          indicators.length > 1 && handleCellMouseLeave
                        }
                        onClick={() =>
                          indicators.length > 1 &&
                          handleIndicatorSelection(
                            !selectedIndicators[indicatorIndex]?.checked,
                            indicatorIndex
                          )
                        }
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {indicators.length > 1 && (
                            <Checkbox
                              size="small"
                              onChange={(e) =>
                                handleIndicatorSelection(
                                  e.target.checked,
                                  indicatorIndex
                                )
                              }
                              checked={
                                selectedIndicators[indicatorIndex].checked
                              }
                            />
                          )}
                          <b>{indicator.title}</b>
                        </div>
                      </HoverTableCell>
                      {categories.map((category, index) => {
                        return (
                          <CategoryCell
                            key={`indicator-category-cell-${index}`}
                            getPropertyTotal={getPropertyTotal}
                            handleCellClick={handleCellClick}
                            handleCellMouseEnter={handleCellMouseEnter}
                            handleCellMouseLeave={handleCellMouseLeave}
                            category={category}
                            indicator={indicator}
                            indicatorIndex={indicatorIndex}
                            getHoveredCellStyle={getHoveredCellStyle}
                          />
                        );
                      })}
                    </TableRow>
                    {indicator.items.map((item, index) => {
                      return (
                        <TableRow hover key={`indicator-item-${index}`}>
                          <HoverTableCell
                            component="th"
                            scope="row"
                            onMouseEnter={() =>
                              handleCellMouseEnter(indicatorIndex, index)
                            }
                            onMouseLeave={handleCellMouseLeave}
                            onClick={() =>
                              handleAreaSelection(
                                !selectedIndicators[
                                  indicatorIndex
                                ].areasChecked.find(
                                  (area) => area.index === index
                                ),
                                indicatorIndex,
                                index
                              )
                            }
                          >
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <Checkbox
                                style={styles.childCheckbox}
                                size="small"
                                onChange={(e) =>
                                  handleAreaSelection(
                                    e.target.checked,
                                    indicatorIndex,
                                    index
                                  )
                                }
                                checked={
                                  selectedIndicators[
                                    indicatorIndex
                                  ].areasChecked.find(
                                    (area) => area.index === index
                                  ) !== undefined
                                }
                              />
                              {item.subtitle}
                            </div>
                          </HoverTableCell>
                          {categories.map((category, indexCategory) => {
                            return (
                              <AreaCell
                                key={`indicator-category-cell-${indexCategory}`}
                                category={category}
                                indicatorIndex={indicatorIndex}
                                index={index}
                                handleCellClick={handleCellClick}
                                handleCellMouseEnter={handleCellMouseEnter}
                                handleCellMouseLeave={handleCellMouseLeave}
                                getHoveredCellStyle={getHoveredCellStyle}
                                item={item}
                              />
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </IndicatorRadioGroup>
      </IndicatorTablePaper>
    </Container>
  );
}
